import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GitCompareArrows } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ANALYTICS_COLORS, ChartCard, EmptyAnalytics } from "@/components/analytics/AnalyticsPrimitives";
import DoxaSelect from "@/components/doxa/DoxaSelect";

const SERIES_COLORS = [ANALYTICS_COLORS.orange, ANALYTICS_COLORS.blue, ANALYTICS_COLORS.green];
const METRICS = [
  ["average_presence", "Frequência média", "%"],
  ["critical_percent", "Alunos críticos", "%"],
  ["video_completion", "Vídeos concluídos", "%"],
  ["material_receipt", "Materiais recebidos", "%"],
  ["push_adoption", "Push ativo", "%"],
];

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs max-w-[250px]"><p className="font-semibold mb-1">{label}</p>{payload.map((item) => <p key={item.dataKey} className="text-[#6B655E]"><strong className="text-[#1F1F1F]">{item.name}:</strong> {item.value == null ? "—" : `${item.value}%`}</p>)}</div>;
}

export default function ClassComparisonView({ classGroups = [], initialClassId }) {
  const initial = useMemo(() => {
    const ids = classGroups.map((row) => row.id);
    const first = initialClassId && ids.includes(initialClassId) ? initialClassId : ids[0];
    const second = ids.find((id) => id !== first) || "";
    return [first || "", second, ""];
  }, [classGroups, initialClassId]);
  const [selected, setSelected] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setSelected(initial); }, [initial]);

  const ids = selected.filter(Boolean).filter((id, index, arr) => arr.indexOf(id) === index);
  useEffect(() => {
    if (ids.length < 2) { setData(null); return; }
    let alive = true;
    setLoading(true);
    setError("");
    api.get("/analytics/compare/classes", { params: { class_ids: ids.join(",") } })
      .then(({ data: response }) => alive && setData(response))
      .catch((err) => alive && setError(formatApiError(err)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [ids.join(",")]);

  const updateSlot = (index, value) => setSelected((current) => current.map((item, i) => i === index ? value : item));
  const classes = data?.classes || [];
  const trend = useMemo(() => {
    const max = Math.max(0, ...classes.map((row) => (row.trend || []).length));
    return Array.from({ length: max }, (_, idx) => {
      const point = { lesson_number: idx + 1 };
      classes.forEach((row, classIndex) => { point[`c${classIndex}`] = row.trend?.[idx]?.percent ?? null; });
      return point;
    });
  }, [classes]);
  const buckets = ["PERFECT", "HIGH", "ATTENTION", "CRITICAL"];
  const bucketLabels = { PERFECT: "100%", HIGH: "90–99%", ATTENTION: "75–89%", CRITICAL: "<75%" };
  const distribution = buckets.map((key) => {
    const row = { bucket: bucketLabels[key] };
    classes.forEach((group, classIndex) => {
      row[`c${classIndex}`] = (group.frequency_distribution || []).find((item) => item.key === key)?.count || 0;
    });
    return row;
  });

  return (
    <div className="space-y-4" data-testid="class-comparison-view">
      <div className="doxa-card p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-4"><div className="w-10 h-10 rounded-xl bg-[#FFEBE3] text-[#F75A1B] flex items-center justify-center shrink-0"><GitCompareArrows className="w-5 h-5" /></div><div><h2 className="font-display text-lg font-bold">Comparar turmas</h2><p className="text-xs text-[#6B655E] mt-1">Selecione 2 ou 3 turmas. A comparação usa apenas indicadores semanticamente compatíveis.</p></div></div>
        <div className="grid md:grid-cols-3 gap-3">
          {[0, 1, 2].map((slot) => (
            <div key={slot}>
              <label className="text-xs text-[#6B655E] block mb-1">{slot === 2 ? "3ª turma (opcional)" : `${slot + 1}ª turma`}</label>
              <DoxaSelect
                value={selected[slot] || ""}
                onValueChange={(value) => updateSlot(slot, value)}
                options={[
                  { value: "", label: slot === 2 ? "Sem terceira turma" : "Selecione…" },
                  ...classGroups.map((group) => ({
                    value: group.id,
                    label: group.name,
                    disabled: selected.some((id, i) => i !== slot && id === group.id),
                  })),
                ]}
                className="w-full"
                ariaLabel={slot === 2 ? "Terceira turma opcional" : `${slot + 1}ª turma`}
              />
            </div>
          ))}
        </div>
      </div>

      {ids.length < 2 ? <EmptyAnalytics title="Selecione duas turmas" description="A comparação aparece quando há pelo menos duas turmas diferentes." /> : loading ? <div className="h-72 rounded-2xl bg-[#F0EBE3] animate-pulse" /> : error ? <EmptyAnalytics title="Não foi possível comparar" description={error} /> : data ? (
        <>
          <ChartCard title="Indicadores lado a lado" subtitle="Percentuais calculados dentro de cada turma, sem criar ranking geral.">
            <div className="overflow-x-auto"><table className="w-full min-w-[680px] text-sm"><thead><tr className="text-xs text-[#6B655E] border-b border-[#EBE5DB]"><th className="text-left py-3 pr-4">Indicador</th>{classes.map((row) => <th key={row.class_group?.id} className="text-center px-3 py-3 max-w-[220px]">{row.class_group?.name}</th>)}</tr></thead><tbody>{METRICS.map(([key, label, suffix]) => <tr key={key} className="border-b border-[#F0EBE3] last:border-0"><td className="py-3 pr-4 font-medium">{label}</td>{classes.map((row) => <td key={row.class_group?.id} className="text-center px-3 py-3 font-display text-lg font-bold">{row.metrics?.[key] == null ? "—" : `${Math.round(row.metrics[key])}${suffix}`}</td>)}</tr>)}</tbody></table></div>
          </ChartCard>

          <div className="grid xl:grid-cols-2 gap-4">
            <ChartCard title="Evolução da presença" subtitle="Eixo X = número sequencial da aula válida de cada turma, não a mesma data.">
              {trend.length ? <div className="h-80"><ResponsiveContainer width="100%" height="100%"><LineChart data={trend} margin={{ top: 8, right: 10, left: -18, bottom: 8 }} accessibilityLayer><CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" /><XAxis dataKey="lesson_number" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} /><Tooltip content={<TooltipBox />} /><Legend wrapperStyle={{ fontSize: 10 }} />{classes.map((row, index) => <Line key={row.class_group?.id} type="monotone" dataKey={`c${index}`} name={row.class_group?.name} stroke={SERIES_COLORS[index]} strokeWidth={3} dot={false} connectNulls />)}</LineChart></ResponsiveContainer></div> : <EmptyAnalytics />}
            </ChartCard>

            <ChartCard title="Distribuição de frequência" subtitle="Quantidade de alunos em cada faixa, por turma.">
              <div className="h-80"><ResponsiveContainer width="100%" height="100%"><BarChart data={distribution} margin={{ top: 8, right: 8, left: -18, bottom: 8 }} accessibilityLayer><CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} /><XAxis dataKey="bucket" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />{classes.map((row, index) => <Bar key={row.class_group?.id} dataKey={`c${index}`} name={row.class_group?.name} fill={SERIES_COLORS[index]} radius={[6, 6, 0, 0]} />)}</BarChart></ResponsiveContainer></div>
            </ChartCard>
          </div>
          <p className="text-xs text-[#6B655E] rounded-xl bg-[#FAF7F1] border border-[#EBE5DB] p-3">{data.methodology?.academic_score_policy} {data.methodology?.trend_axis}</p>
        </>
      ) : null}
    </div>
  );
}
